import React, { useState, useEffect } from 'react';
import { Check, X, ExternalLink, Clock, DollarSign, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog, AdminPageHeader } from '../../components/AdminUi';

// Blockchain explorer links by network
const EXPLORERS = {
  tron: 'https://tronscan.org/#/transaction/',
  trc20: 'https://tronscan.org/#/transaction/',
  eth: 'https://etherscan.io/tx/',
  erc20: 'https://etherscan.io/tx/',
  bsc: 'https://bscscan.com/tx/',
  bnb: 'https://bscscan.com/tx/',
  btc: 'https://mempool.space/tx/',
  ltc: 'https://blockchair.com/litecoin/transaction/',
};

function explorerLink(network, txHash) {
  if (!txHash || !network) return null;
  const key = network.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const [k, url] of Object.entries(EXPLORERS)) {
    if (key.includes(k)) return url + txHash;
  }
  return null;
}

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reviewAction, setReviewAction] = useState(null);

  useEffect(() => { fetchPayments(); }, [statusFilter]); // eslint-disable-line

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      setData(await apiGet(`${API_ROUTES.admin.payments}?${params}`));
    } catch (e) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const completeReview = async (note = '') => {
    if (!reviewAction) return;
    const { payment, action } = reviewAction;
    try {
      const endpoint = action === 'approve'
        ? `${API_ROUTES.admin.payments}/${payment.id}/approve`
        : `${API_ROUTES.admin.payments}/${payment.id}/reject`;
      const res = await apiFetch(endpoint, {
        method: 'PATCH',
        body: action === 'reject' ? { reason: note } : undefined,
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(action === 'approve' ? 'Payment approved, subscription activated' : 'Payment rejected');
      setReviewAction(null);
      fetchPayments();
    } catch (e) {
      toast.error(action === 'approve' ? 'Failed to approve payment' : 'Failed to reject payment');
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-500/20 text-amber-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
    };
    return map[status] || 'bg-gray-700 text-gray-400';
  };

  return (
    <div>
      <AdminPageHeader
        title="Payments"
        subtitle="Review payment requests, inspect transaction hashes, and activate subscriptions"
        action={(
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-dark-card border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        )}
      />

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-amber-400/10 to-amber-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-amber-400" />
              <span className="text-xs text-gray-500 uppercase">Pending Review</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{data.pendingCount}</p>
          </div>
          <div className="bg-gradient-to-br from-green-400/10 to-green-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={16} className="text-green-400" />
              <span className="text-xs text-gray-500 uppercase">Approved (24h)</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{data.approvedToday}</p>
          </div>
        </div>
      )}

      {/* Payments table */}
      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border text-gray-500 text-xs uppercase">
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">TX</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-center px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">Loading...</td></tr>
              ) : !data?.payments?.length ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">No payments found</td></tr>
              ) : (
                data.payments.map((p) => {
                  const exLink = explorerLink(p.network, p.txHash);
                  return (
                    <tr key={p.id} className="border-b border-dark-border/50 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <p className="text-gray-200 font-medium">{p.username || 'Unknown'}</p>
                        <p className="text-gray-500 text-xs">{p.userEmail}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-300">{p.planSlug}</span>
                        <span className="text-gray-600 text-xs block">{p.billingCycle}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-200 font-medium">${p.amountUsd}</span>
                        {p.originalAmount !== p.amountUsd && (
                          <span className="text-gray-600 text-xs line-through block">${p.originalAmount}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {p.txHash ? (
                          exLink ? (
                            <a href={exLink} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 flex items-center gap-1 text-xs">
                              <Eye size={12} /> {p.txHash.slice(0, 10)}... <ExternalLink size={12} />
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs font-mono">{p.txHash.slice(0, 8)}...</span>
                          )
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs ${statusBadge(p.status)}`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{p.createdAt?.split('T')[0]}</td>
                      <td className="px-4 py-3">
                        {p.status === 'pending' ? (
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => setReviewAction({ action: 'approve', payment: p })} className="p-1.5 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20" title="Approve"><Check size={15} /></button>
                            <button onClick={() => setReviewAction({ action: 'reject', payment: p })} className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20" title="Reject"><X size={15} /></button>
                          </div>
                        ) : (
                          <span className="text-gray-600 text-xs block text-center">{p.adminNote || '—'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <AdminConfirmDialog
        open={Boolean(reviewAction)}
        tone={reviewAction?.action === 'approve' ? 'success' : 'danger'}
        title={reviewAction?.action === 'approve' ? 'Approve payment?' : 'Reject payment?'}
        message={reviewAction ? `${reviewAction.payment.username || reviewAction.payment.userEmail || 'User'} - ${reviewAction.payment.planSlug} ${reviewAction.payment.billingCycle}, ${reviewAction.payment.amountUsd}` : ''}
        confirmLabel={reviewAction?.action === 'approve' ? 'Approve & Activate' : 'Reject Payment'}
        noteLabel={reviewAction?.action === 'reject' ? 'Rejection reason' : undefined}
        notePlaceholder="Explain what the user needs to fix"
        onConfirm={completeReview}
        onCancel={() => setReviewAction(null)}
      />
    </div>
  );
}
